if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      connectedCallback() {
        this.moveToBody();
      }

      moveToBody() {
        if (this.parentElement === document.body) return;

        const section = this.closest('.shopify-section');
        if (section?.id) {
          this.dataset.section = section.id.replace('shopify-section-', '');
        }

        document.body.appendChild(this);
        this.moved = true;
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        this.moveToBody();

        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            super.show(opener);

            requestAnimationFrame(() => {
              const galleryViewer = this.modalContent.querySelector(
                'slider-component[id^="GalleryViewer"], product-gallery-infinite-slider[id^="GalleryViewer"]'
              );
              galleryViewer?.resetPages?.();
            });
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.revealProductAccordions(productElement);
        this.removePostBuyButtonBlocks(productElement);
        this.removeGalleryListSemantic(productElement);
        this.setupQuickAddGallery(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
        this.mountProductMediaModal(productElement);
      }

      mountProductMediaModal(productElement) {
        const productModal = productElement.querySelector('product-modal');
        if (!productModal) return;

        if (productModal.id) {
          document.querySelectorAll(`body > #${CSS.escape(productModal.id)}`).forEach((existing) => {
            if (existing !== productModal) existing.remove();
          });
        }

        if (productModal.parentElement !== document.body) {
          document.body.appendChild(productModal);
        }

        customElements.upgrade(productModal);
      }

      setupQuickAddGallery(productElement) {
        const galleryViewer = productElement.querySelector(
          'slider-component[id^="GalleryViewer"], product-gallery-infinite-slider[id^="GalleryViewer"]'
        );
        if (!galleryViewer) return;

        const galleryList = galleryViewer.querySelector('[id^="Slider-Gallery"]');
        const sliderButtons = galleryViewer.querySelector('.slider-buttons');

        if (galleryList) {
          galleryList.classList.add('slider--everywhere');
          galleryList.classList.remove('slider--mobile');

          galleryList.querySelectorAll('.is-adjacent, .is-preview').forEach((item) => {
            item.classList.remove('is-adjacent', 'is-preview');
          });
        }

        if (sliderButtons) {
          sliderButtons.classList.remove('quick-add-hidden', 'small-hide', 'medium-hide', 'large-up-hide');
        }

        productElement.querySelectorAll('modal-opener.product__modal-opener--image').forEach((opener) => {
          const toggle = opener.querySelector('.product__media-toggle');
          const icon = opener.querySelector('.product__media-icon');
          if (!toggle || !icon) return;

          icon.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            toggle.click();
          });
        });

        const thumbnailSlider = productElement.querySelector(
          'slider-component[id^="GalleryThumbnails"], product-gallery-infinite-slider[id^="GalleryThumbnails"]'
        );
        if (thumbnailSlider) thumbnailSlider.remove();
      }

      revealProductAccordions(productElement) {
        productElement.querySelectorAll('.product__accordion').forEach((accordion) => {
          accordion.classList.remove('quick-add-hidden');
        });
      }

      removePostBuyButtonBlocks(productElement) {
        const productForm = productElement.querySelector('product-form');
        if (!productForm) return;

        const buyButtonsBlock = productForm.parentElement;
        if (!buyButtonsBlock) return;

        let sibling = buyButtonsBlock.nextElementSibling;
        while (sibling) {
          const next = sibling.nextElementSibling;
          if (!sibling.classList.contains('product__accordion')) {
            sibling.remove();
          }
          sibling = next;
        }
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const breadcrumb = productElement.querySelector('.breadcrumb');
        if (breadcrumb) breadcrumb.remove();

        const buyWithCollection = productElement.querySelector('.product__buy-with-collection');
        if (buyWithCollection) buyWithCollection.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );

  function mountProductGridQuickAddModals() {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;

    container.querySelectorAll('quick-add-modal, modal-dialog.quick-add-modal').forEach((modal) => {
      if (modal.id) {
        document.querySelectorAll(`body > #${CSS.escape(modal.id)}`).forEach((orphan) => {
          if (orphan !== modal) orphan.remove();
        });
      }

      customElements.upgrade(modal);
      if (typeof modal.moveToBody === 'function') {
        modal.moveToBody();
      } else if (modal.parentElement !== document.body) {
        document.body.appendChild(modal);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', mountProductGridQuickAddModals);
  document.addEventListener('facets:grid-rendered', mountProductGridQuickAddModals);
}
